import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TableColumn, TableAction } from 'src/app/shared/components/data-table/data-table.component';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-tenders',
  templateUrl: './tenders.component.html',
  styleUrls: ['./tenders.component.scss']
})
export class TendersComponent implements OnInit {
  AppConstants = AppConstants;
  tenders: any[] = [];
  isFwaPracticeMode: boolean = false;
  tableColumns: TableColumn[] = [];
  showConfirmDialog: boolean = false;
  pendingAction: 'Submit' | 'Approve' | 'Publish' | null = null;
  pendingTenderId: string | null = null;

  constructor(
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    private _router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isFwaPracticeMode = this._router.url.includes('/fwa-practice');
    this.initializeTableColumns();
    this.fetchTenders();
  }

  private initializeTableColumns(): void {
    const actionIcons: TableAction[] = [
      {
        iconPath: 'assets/icons/eye-icon.svg',
        action: 'view',
        title: 'View'
      },
      {
        iconPath: 'assets/icons/edit-icon.svg',
        action: 'edit',
        title: 'Edit'
      },
      {
        icon: `<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.99935 18.3332C14.6017 18.3332 18.3327 14.6022 18.3327 9.99984C18.3327 5.39746 14.6017 1.6665 9.99935 1.6665C5.39698 1.6665 1.66602 5.39746 1.66602 9.99984C1.66602 14.6022 5.39698 18.3332 9.99935 18.3332Z" stroke="#4A5565" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.5 10.0002L9.16667 11.6668L12.5 8.3335" stroke="#4A5565" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
        action: 'approve',
        title: 'Approve',
        condition: (row: any) => this.shouldShowAction(row, 'approve')
      },
      {
        icon: `<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.99935 18.3332C14.6017 18.3332 18.3327 14.6022 18.3327 9.99984C18.3327 5.39746 14.6017 1.6665 9.99935 1.6665C5.39698 1.6665 1.66602 5.39746 1.66602 9.99984C1.66602 14.6022 5.39698 18.3332 9.99935 18.3332Z" stroke="#4A5565" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.5 10.0002L9.16667 11.6668L12.5 8.3335" stroke="#4A5565" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
        action: 'publish',
        title: 'Publish',
        condition: (row: any) => this.shouldShowAction(row, 'publish')
      }
    ];

    this.tableColumns = [
      {
        field: 'tenderId',
        header: this.isFwaPracticeMode ? 'FWA-Practice ID' : 'Tender ID',
        type: 'text'
      },
      {
        field: 'title',
        header: 'Title',
        type: 'text'
      },
      {
        field: 'linkedPR',
        header: 'Linked PR',
        type: 'link'
      },
      {
        field: 'type',
        header: 'Type',
        type: 'badge',
        badgeClass: 'type-badge'
      },
      {
        field: 'submissionDeadline',
        header: 'Submission Deadline',
        type: 'date',
        format: 'dd-MM-yyyy'
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeClass: 'status-badge'
      },
      {
        field: 'workflowStatus',
        header: 'Workflow Status',
        type: 'badge',
        badgeClass: 'workflow-status-badge'
      },
      {
        field: 'vendorsInvited',
        header: 'Vendors Invited',
        type: 'number'
      },
      {
        field: 'actions',
        header: 'Actions',
        type: 'action',
        actions: actionIcons
      }
    ];
  }

  async fetchTenders(): Promise<void> {
    this._loaderService.showLoader();

    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const controller = AppConstants.CONTROLLERS.TENDERS;
      const query = this.isFwaPracticeMode ? 'scope=1' : 'scope=2';
      const url = `${environment.API_URL}/api/${controller}/${AppConstants.ACTIONS.GetTenderListData}?${query}`;
      const response: any = await lastValueFrom(
        this.apiService.getByUrl(url)
      );

      // Check if request was successful
      if (!response?.isSuccess && !response?.IsSuccess) {
        const errorMsg = response?.errorMessageEn || response?.ErrorMessageEn || response?.errorMessageAr || response?.ErrorMessageAr || `Failed to fetch ${this.entityLabelPluralLower}`;
        console.error(`[Tenders] API Error: ${errorMsg}`);
        this.tenders = [];
        this.cdr.detectChanges();
        return;
      }

      // Extract tenders data from response
      const tendersData = response?.data || [];
      
      if (Array.isArray(tendersData) && tendersData.length > 0) {
        this.tenders = tendersData.map((tender: any) => this.mapToTenderData(tender));
      } else {
        this.tenders = [];
      }
      
      this.cdr.detectChanges();
      
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error occurred';
      console.error(`[Tenders] Exception while fetching tenders:`, errorMessage, error);
      this.tenders = [];
      this.cdr.detectChanges();
    } finally {
      this._loaderService.hideLoader();
    }
  }

  /**
   * Maps raw API response data to the tender data structure expected by the component
   */
  private mapToTenderData(tender: any): any {
    return {
      tenderId: tender.tenderID || tender.tenderId || '',
      title: tender.tenderName || tender.description || tender.TenderName || '',
      linkedPR: tender.linkedPR || tender.purchaseRequisition || tender.PurchaseRequisition || '',
      type: tender.typeofTender || tender.tenderType || '',
      submissionDeadline: this.parseDate(tender.acceptanceDeadline),
      status: tender.status || '',
      workflowStatus: this.formatWorkflowStatus(tender.workflowStatus || tender.WorkflowStatus || tender.workFlowStatus || tender.WorkFlowStatus || ''),
      vendorsInvited: tender.totalInvitedMembers || tender.TotalInvitedMembers || 0
    };
  }

  formatWorkflowStatus(status: any): string {
    if (!status) return 'Not Submitted';
    
    // Handle enum values (numbers)
    if (typeof status === 'number') {
      switch(status) {
        case 0: return 'Not Submitted';
        case 1: return 'Pending Publication';
        case 2: return 'Approved';
        case 3: return 'Rejected';
        case 4: return 'Published';
        default: return 'Unknown';
      }
    }
    
    // Handle string values
    const statusStr = String(status).trim();
    if (!statusStr) return 'Not Submitted';
    
    // Convert common formats to display format
    switch(statusStr.toLowerCase()) {
      case 'notsubmitted':
      case 'not_submitted':
        return 'Not Submitted';
      case 'pendingapproval':
      case 'pending_approval':
        return 'Pending Publication';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'published':
        return 'Published';
      default:
        // Return formatted version (e.g., "PendingApproval" -> "Pending Approval")
        return statusStr.replace(/([A-Z])/g, ' $1').trim();
    }
  }

  /**
   * Parses a date value into a Date object
   * Returns null if the date value is null or invalid
   */
  private parseDate(dateValue: any): Date | null {
    if (!dateValue || dateValue === null) {
      return null;
    }
    
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    try {
      const parsedDate = new Date(dateValue);
      // Check if the parsed date is valid
      if (isNaN(parsedDate.getTime())) {
        return null;
      }
      return parsedDate;
    } catch {
      return null;
    }
  }

  get entityLabel(): string {
    return this.isFwaPracticeMode ? 'FWA-Practice' : 'Tender';
  }

  get entityLabelPlural(): string {
    return this.isFwaPracticeMode ? 'FWA-Practices' : 'Tenders';
  }

  get entityLabelPluralLower(): string {
    return this.entityLabelPlural.toLowerCase();
  }

  createTender(): void {
    this._router.navigate([this.isFwaPracticeMode ? AppConstants.ROUTES.FWA_PRACTICE : AppConstants.ROUTES.TENDER, this.isFwaPracticeMode ? AppConstants.ROUTES.CREATE_PRACTICE : AppConstants.ROUTES.CREATE_TENDER]);
  }

  uploadTenders(): void {
    // TODO: Implement upload functionality
    console.log(`Upload ${this.entityLabelPlural}`);
  }

  onTableActionClick(event: {action: string, row: any}): void {
    switch(event.action) {
      case 'view':
        this.viewTender(event.row.tenderId);
        break;
      case 'edit':
        this.editTender(event.row.tenderId);
        break;
      case 'document':
        this.viewDocuments(event.row.tenderId);
        break;
      case 'submit':
        this.submitTender(event.row.tenderId);
        break;
      case 'approve':
        this.approveTender(event.row.tenderId);
        break;
      case 'publish':
        this.publishTender(event.row.tenderId);
        break;
    }
  }

  onTableLinkClick(event: {field: string, row: any}): void {
    if (event.field === 'tenderId') {
      this.viewTender(event.row.tenderId);
    } else if (event.field === 'linkedPR') {
      // TODO: Navigate to PR details
      console.log('Navigate to PR:', event.row.linkedPR);
    }
  }

  viewTender(tenderId: string): void {
    this._router.navigate([`/${this.isFwaPracticeMode ? AppConstants.ROUTES.FWA_PRACTICE : AppConstants.ROUTES.TENDER}/${this.isFwaPracticeMode ? AppConstants.ROUTES.PRACTICEDETAILS : AppConstants.ROUTES.TENDERDETAILS}/${tenderId}`]);
  }

  editTender(tenderId: string): void {
    // Navigate to tender details page with edit mode
    this._router.navigate([`/${this.isFwaPracticeMode ? AppConstants.ROUTES.FWA_PRACTICE : AppConstants.ROUTES.TENDER}/${this.isFwaPracticeMode ? AppConstants.ROUTES.PRACTICEDETAILS : AppConstants.ROUTES.TENDERDETAILS}/${tenderId}`], {
      queryParams: { edit: 'true' }
    });
  }

  viewDocuments(tenderId: string): void {
    // TODO: Open documents dialog
    console.log('View Documents:', tenderId);
  }

  shouldShowAction(row: any, action: 'submit' | 'approve' | 'publish'): boolean {
    const status = (row.status || '').toLowerCase();
    
    // Submit: Show for draft or initial status
    if (action === 'submit') {
      return status === 'draft' || status === '' || status === 'in progress' || status === 'pending';
    }
    
    // Approve: Show after submit (when status is submitted or under evaluation)
    if (action === 'approve') {
      return status === 'submitted' || status === 'under evaluation' || status === 'evaluation';
    }
    
    // Publish: Show when ready to publish (after approval)
    if (action === 'publish') {
      return status === 'approved' || status === 'confirmed' || status === 'ready to publish';
    }
    
    return false;
  }

  submitTender(tenderId: string): void {
    this.pendingAction = 'Submit';
    this.pendingTenderId = tenderId;
    this.showConfirmDialog = true;
  }

  approveTender(tenderId: string): void {
    this.pendingAction = 'Approve';
    this.pendingTenderId = tenderId;
    this.showConfirmDialog = true;
  }

  publishTender(tenderId: string): void {
    this.pendingAction = 'Publish';
    this.pendingTenderId = tenderId;
    this.showConfirmDialog = true;
  }

  async confirmWorkflowAction(): Promise<void> {
    if (!this.pendingAction || !this.pendingTenderId) return;

    const action = this.pendingAction;
    const tenderId = this.pendingTenderId;
    
    this.showConfirmDialog = false;
    this.pendingAction = null;
    this.pendingTenderId = null;

    switch (action) {
      case 'Submit':
        await this.executeSubmitTender(tenderId);
        break;
      case 'Approve':
        this.executeApproveTender(tenderId);
        break;
      case 'Publish':
        this.executePublishTender(tenderId);
        break;
    }
  }

  cancelWorkflowAction(): void {
    this.showConfirmDialog = false;
    this.pendingAction = null;
    this.pendingTenderId = null;
  }

  getConfirmDialogMessage(): string {
    if (!this.pendingAction) return '';
    return `Are you sure you want to ${this.pendingAction} this ${this.entityLabel.toLowerCase()}?`;
  }

  private async executeSubmitTender(tenderId: string): Promise<void> {
    try {
      this._loaderService.showLoader();

      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.SubmitTender}`;
      const payload = {
        TenderNumber: tenderId
      };

      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      if (response && (response.IsSuccess || response.isSuccess || response.data)) {
        this.toasterService.showSuccess(`${this.entityLabel} submitted successfully`, 'Success');
        await this.fetchTenders();
      } else {
        const errorMsg = response?.ErrorMessageEn || response?.errorMessageEn || response?.ErrorMessage || `Failed to submit ${this.entityLabel.toLowerCase()}`;
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      const errorMsg = error?.error?.ErrorMessageEn || error?.message || `An error occurred while submitting the ${this.entityLabel.toLowerCase()}`;
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Tenders] Error submitting tender:', error);
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private executeApproveTender(tenderId: string): void {
    // TODO: Implement approve tender functionality
    console.log('Approve Tender:', tenderId);
    // After successful approve, refresh the tenders list
    // this.fetchTenders();
  }

  private executePublishTender(tenderId: string): void {
    // TODO: Implement publish tender functionality
    console.log('Publish Tender:', tenderId);
    // After successful publish, refresh the tenders list
    // this.fetchTenders();
  }
}
